import { useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { typedResolver } from '@/lib/typedResolver';
import { z } from 'zod';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@marine/ui/sheet';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Textarea } from '@marine/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@marine/ui/select';
import { useCreateTenant, useUpdateTenant } from '../hooks/useTenants';
import type { Tenant } from '../types/platform.types';

const baseTenantFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  domain: z.string()
    .min(3, 'Domain must be at least 3 characters')
    .max(50, 'Domain must not exceed 50 characters') // PostgreSQL max identifier: 63 - "tenant_" (7) - buffer (6) = 50
    .regex(/^[a-z0-9-]+$/, 'Domain must contain only lowercase letters, numbers, and hyphens')
    .regex(/^[a-z]/, 'Domain must start with a letter')
    .regex(/[a-z0-9]$/, 'Domain must end with a letter or number'),
  primary_location_name: z.string().min(2, 'Primary location name is required').max(255),
  legal_name: z.string().optional(),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  business_type: z.enum(['dealership', 'distributor', 'service_center']),
  subscription_plan: z.enum(['basic', 'professional', 'enterprise']),
  tax_id: z.string().optional(),
  license_number: z.string().optional(),
  notes: z.string().optional(),
});

type TenantFormData = z.infer<typeof baseTenantFormSchema> & { primary_location_name?: string };

interface TenantFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenant?: Tenant | null;
}

export function TenantFormSheet({ open, onOpenChange, tenant }: TenantFormSheetProps) {
  const isEditing = !!tenant;
  const { mutate: createTenant, isPending: isCreating } = useCreateTenant();
  const { mutate: updateTenant, isPending: isUpdating } = useUpdateTenant();
  const formSchema = useMemo(
    () => (isEditing ? baseTenantFormSchema.partial({ primary_location_name: true }) : baseTenantFormSchema),
    [isEditing],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<TenantFormData>({
    resolver: typedResolver(formSchema) as Resolver<TenantFormData>,
    defaultValues: tenant ? {
      name: tenant.name,
      domain: tenant.domain || '',
      primary_location_name: '',
      legal_name: tenant.legal_name || '',
      email: tenant.email,
      phone: tenant.phone || '',
      website: tenant.website || '',
      business_type: tenant.business_type,
      subscription_plan: tenant.subscription_plan,
      tax_id: tenant.tax_id || '',
      license_number: tenant.license_number || '',
      notes: tenant.notes || '',
    } : {
      business_type: 'dealership',
      subscription_plan: 'basic',
    },
  });

  // Generate domain slug from name when name field loses focus
  const handleNameBlur = () => {
    if (!isEditing) {
      const name = getValues('name');
      const currentDomain = getValues('domain');
      
      // Only auto-generate if domain is empty or hasn't been manually edited
      if (name && !currentDomain) {
        let slug = name
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-')          // Replace spaces with hyphens
          .replace(/-+/g, '-')           // Replace multiple hyphens with single
          .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
        
        // Truncate to 50 chars (PostgreSQL max identifier is 63, minus "tenant_" prefix and buffer = 50)
        if (slug.length > 50) {
          slug = slug.substring(0, 50).replace(/-+$/, ''); // Remove trailing hyphen if truncated mid-word
        }
        
        setValue('domain', slug);
      }
    }
  };

  const onSubmit = (data: TenantFormData) => {
    if (isEditing) {
      updateTenant(
        { id: tenant.id, data },
        {
          onSuccess: () => {
            onOpenChange(false);
            reset();
          },
        }
      );
    } else {
      createTenant(data, {
        onSuccess: () => {
          onOpenChange(false);
          reset();
        },
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent variant="panel" className="lg:w-[900px]">
        {/* Header */}
        <SheetHeader className="border-b py-3 px-5 border-border">
          <SheetTitle className="text-sm font-medium">
            {isEditing ? 'Edit Tenant' : 'Create New Tenant'}
          </SheetTitle>
        </SheetHeader>

        {/* Body */}
        {/* Part of the sheet's vertical flex chain: without flex/min-h-0 here the
            form is a plain block, SheetBody's flex-1 has nothing to measure
            against and the body cannot scroll. This is what the old
            h-[calc(100dvh-10rem)] on the ScrollArea was really working around. */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-1 flex-col min-h-0">
          <SheetBody className="px-1.5 py-0">
              <div className="px-5 py-5 space-y-8">
                {/* Basic Information */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3 className="text-lg font-semibold">Basic Information</h3>
                    <p className="text-sm text-muted-foreground">Essential tenant details</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-medium">
                        Tenant Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="name"
                        {...register('name')}
                        onBlur={handleNameBlur}
                        placeholder="e.g., Ocean Marine Dealership"
                        className={errors.name ? 'border-destructive' : ''}
                      />
                      {errors.name && (
                        <p className="text-xs text-destructive">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="domain" className="text-sm font-medium">
                        Domain / Slug <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="domain"
                        {...register('domain')}
                        placeholder="e.g., coastal-marine"
                        maxLength={50}
                        className={errors.domain ? 'border-destructive' : ''}
                      />
                      {errors.domain && (
                        <p className="text-xs text-destructive">{errors.domain.message}</p>
                      )}
                    </div>

                    {!isEditing && (
                      <div className="space-y-2">
                        <Label htmlFor="primary_location_name" className="text-sm font-medium">
                          Primary Location Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="primary_location_name"
                          {...register('primary_location_name')}
                          placeholder="e.g., Main Showroom"
                          className={errors.primary_location_name ? 'border-destructive' : ''}
                        />
                        {errors.primary_location_name && (
                          <p className="text-xs text-destructive">{errors.primary_location_name.message}</p>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="legal_name" className="text-sm font-medium">Legal Name</Label>
                      <Input
                        id="legal_name"
                        {...register('legal_name')}
                        placeholder="e.g., Ocean Marine Inc."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-medium">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        {...register('email')}
                        placeholder="contact@oceanmarine.com"
                        className={errors.email ? 'border-destructive' : ''}
                      />
                      {errors.email && (
                        <p className="text-xs text-destructive">{errors.email.message}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
                      <Input
                        id="phone"
                        {...register('phone')}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="website" className="text-sm font-medium">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        {...register('website')}
                        placeholder="https://www.oceanmarine.com"
                        className={errors.website ? 'border-destructive' : ''}
                      />
                      {errors.website && (
                        <p className="text-xs text-destructive">{errors.website.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3 className="text-lg font-semibold">Business Details</h3>
                    <p className="text-sm text-muted-foreground">Business classification and subscription</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_type" className="text-sm font-medium">
                        Business Type <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={watch('business_type')}
                        onValueChange={(value) => setValue('business_type', value as TenantFormData['business_type'])}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select business type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dealership">Dealership</SelectItem>
                          <SelectItem value="distributor">Distributor</SelectItem>
                          <SelectItem value="service_center">Service Center</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subscription_plan" className="text-sm font-medium">
                        Subscription Plan <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={watch('subscription_plan')}
                        onValueChange={(value) => setValue('subscription_plan', value as TenantFormData['subscription_plan'])}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select plan" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Basic - $99/month</SelectItem>
                          <SelectItem value="professional">Professional - $199/month</SelectItem>
                          <SelectItem value="enterprise">Enterprise - $399/month</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tax_id" className="text-sm font-medium">Tax ID / EIN</Label>
                      <Input
                        id="tax_id"
                        {...register('tax_id')}
                        placeholder="XX-XXXXXXX"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="license_number" className="text-sm font-medium">Business License</Label>
                      <Input
                        id="license_number"
                        {...register('license_number')}
                        placeholder="DLR-12345"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Notes */}
                <div className="space-y-4">
                  <div className="border-b pb-2">
                    <h3 className="text-lg font-semibold">Additional Notes</h3>
                    <p className="text-sm text-muted-foreground">Optional notes and comments</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes" className="text-sm font-medium">Notes</Label>
                    <Textarea
                      id="notes"
                      {...register('notes')}
                      placeholder="Add any additional notes about this tenant..."
                      rows={4}
                      className="resize-none"
                    />
                  </div>
                </div>
              </div>
          </SheetBody>

          {/* Footer */}
          <SheetFooter className="flex-row border-t py-2.5 px-5 border-border gap-2.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onOpenChange(false);
                reset();
              }}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={isPending}>
              {isPending ? (
                <>
                  <span className="mr-2">Saving...</span>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </>
              ) : (
                isEditing ? 'Update Tenant' : 'Create Tenant'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
